import React, { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Stack,
  Typography,
} from '@mui/material';
import CleaningServicesIcon from '@mui/icons-material/CleaningServices';

import { OperationService } from '../../../services';
import type { OperationResult, SourceImageRef } from '../../../domain';
import {
  DEFAULT_REMOVE_OBJECT_STATE,
  buildRemoveObjectRequest,
  hasSourceImage,
  type RemoveObjectFormState,
} from './buildRequests';
import { useOperationMutation } from './useOperationMutation';
import { submitErrorMessage } from '../../../components/auth/signInRequiredError';

interface Props {
  ensureSession: () => Promise<string>;
  isCreatingSession?: boolean;
  source: SourceImageRef | undefined;
  maskDataUrl: string | null;
  onResult?: (result: OperationResult) => void;
  onRequestClearMask?: () => void;
}

export const RemoveObjectPanel: React.FC<Props> = ({
  ensureSession,
  isCreatingSession = false,
  source,
  maskDataUrl,
  onResult,
  onRequestClearMask,
}) => {
  const [state] = useState<RemoveObjectFormState>(DEFAULT_REMOVE_OBJECT_STATE);
  const [validationError, setValidationError] = useState<string | null>(null);

  const mutation = useOperationMutation(OperationService.removeObject);

  const hasSource = hasSourceImage(source);
  const hasMask = !!maskDataUrl;

  const handleSubmit = async () => {
    if (!source) {
      setValidationError('A source image is required to remove an object.');
      return;
    }
    try {
      const sid = await ensureSession();
      const request = buildRemoveObjectRequest(state, sid, source, maskDataUrl);
      setValidationError(null);
      const result = await mutation.mutateAsync(request);
      onResult?.(result);
    } catch (err) {
      setValidationError(submitErrorMessage(err));
    }
  };

  const submitDisabled =
    mutation.isPending || isCreatingSession || !hasSource || !hasMask;

  return (
    <Stack spacing={1.5}>
      <Stack direction="row" spacing={1} alignItems="center">
        <CleaningServicesIcon fontSize="small" />
        <Typography variant="subtitle2">Remove object</Typography>
      </Stack>

      {!hasSource && (
        <Alert severity="info">Add or select a source image to clean up.</Alert>
      )}
      {hasSource && !hasMask && (
        <Alert severity="info">Paint a mask over the object to remove.</Alert>
      )}

      {validationError && <Alert severity="warning">{validationError}</Alert>}
      {mutation.isError && (
        <Alert severity="error">
          {mutation.error instanceof Error ? mutation.error.message : 'Remove object failed.'}
        </Alert>
      )}

      <Stack direction="row" spacing={1}>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={submitDisabled}
          startIcon={
            mutation.isPending || isCreatingSession ? (
              <CircularProgress size={16} />
            ) : (
              <CleaningServicesIcon />
            )
          }
        >
          {isCreatingSession
            ? 'Starting session…'
            : mutation.isPending
              ? 'Removing…'
              : 'Remove object'}
        </Button>
        {onRequestClearMask && (
          <Button
            variant="outlined"
            size="small"
            onClick={onRequestClearMask}
            disabled={!hasMask || mutation.isPending}
          >
            Clear mask
          </Button>
        )}
      </Stack>

      <Box>
        <Typography variant="caption" color="text.secondary">
          The masked region is replaced with content that matches its surroundings.
        </Typography>
      </Box>
    </Stack>
  );
};
